// src/pages/Dashboard/Dashboard.js
// Admin dashboard — all data comes live from GET /api/dashboard/summary.
// Adding, editing, closing or deleting a requirement (or any application change)
// shows up here on the next refresh (auto every 60s, on tab focus, or the Refresh button).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';
import GraphicalDashboard, {
  groupSources,
  groupSourceCounts,
  visiblePipeline,
  hasVacancyData,
} from './GraphicalDashboard';
import { exportPdf, downloadCsv, describeFilters } from './exportReport';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.jmkinfosoft.com';
const AUTO_REFRESH_MS = 60000;
const VIEW_KEY = 'jmkDashboardView';

const readSavedView = () => {
  try {
    return localStorage.getItem(VIEW_KEY) === 'graphical' ? 'graphical' : 'statistical';
  } catch {
    return 'statistical';
  }
};

const EMPTY_FILTERS = {
  from_date: '',
  to_date: '',
  department: '',
  requirement_status: '',
  requirement_id: '',
};

const LABELS = { whatsapp: 'WhatsApp', sms: 'SMS' };

const humanize = (key) =>
  LABELS[key] ||
  String(key || 'unknown')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
const pct = (part, whole) => (whole ? Math.round((part / whole) * 100) : 0);

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatPeriod = (period, granularity) => {
  if (granularity === 'day') {
    const d = new Date(`${period}T00:00:00`);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  }
  const [y, m] = period.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-IN', {
    month: 'short',
    year: '2-digit',
  });
};

const currentMonth = () => new Date().toISOString().slice(0, 7);

// ─────────────────────────────── small building blocks ───────────────────────────────

const Kpi = ({ icon, label, value, sub, onClick }) => (
  <button type="button" className={`jd-kpi ${onClick ? 'is-link' : ''}`} onClick={onClick} disabled={!onClick}>
    <span className="jd-kpi-icon"><i className={`fas ${icon}`} /></span>
    <span className="jd-kpi-body">
      <span className="jd-kpi-label">{label}</span>
      <span className="jd-kpi-value">{value}</span>
      {sub && <span className="jd-kpi-sub">{sub}</span>}
    </span>
  </button>
);

const Card = ({ title, subtitle, action, children, className = '' }) => (
  <section className={`jd-card ${className}`}>
    <header className="jd-card-head">
      <div>
        <h3>{title}</h3>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {action}
    </header>
    <div className="jd-card-body">{children}</div>
  </section>
);

const Empty = ({ text = 'No data for the selected filters' }) => (
  <div className="jd-empty"><i className="fas fa-inbox" /> {text}</div>
);

/** Horizontal bar list — one hue, value labels on every row. */
const BarList = ({ items, total, muted = [] }) => {
  const max = Math.max(...items.map((i) => i.count), 1);
  if (!items.length || items.every((i) => !i.count)) return <Empty />;
  return (
    <ul className="jd-barlist">
      {items.map((item) => (
        <li key={item.key} title={`${humanize(item.key)}: ${fmt(item.count)}${total ? ` (${pct(item.count, total)}%)` : ''}`}>
          <span className="jd-bar-label">{item.label || humanize(item.key)}</span>
          <span className="jd-bar-track">
            <span
              className={`jd-bar-fill ${muted.includes(item.key) ? 'is-muted' : ''}`}
              style={{ width: `${(item.count / max) * 100}%` }}
            />
          </span>
          <span className="jd-bar-value">
            {fmt(item.count)}
            {total ? <em>{pct(item.count, total)}%</em> : null}
          </span>
        </li>
      ))}
    </ul>
  );
};

/** Segmented 100% bar with legend underneath (labels + counts, never colour alone).
 *  Items may carry their own `color` / `label` (used for application sources). */
const Segments = ({ items, tones = {} }) => {
  const total = items.reduce((s, i) => s + i.count, 0);
  if (!total) return <Empty />;
  return (
    <div className="jd-seg">
      <div className="jd-seg-bar">
        {items.filter((i) => i.count).map((i) => (
          <span
            key={i.key}
            className={`jd-seg-part tone-${tones[i.key] || 'teal'}`}
            style={{ flexGrow: i.count, ...(i.color ? { background: i.color } : {}) }}
            title={`${i.label || humanize(i.key)}: ${fmt(i.count)} (${pct(i.count, total)}%)`}
          />
        ))}
      </div>
      <ul className="jd-legend">
        {items.map((i) => (
          <li key={i.key}>
            <span className={`jd-dot tone-${tones[i.key] || 'teal'}`} style={i.color ? { background: i.color } : undefined} />
            {i.label || humanize(i.key)} <strong>{fmt(i.count)}</strong>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Column chart for the application trend, stacked by every source found in the data. */
const TrendChart = ({ trend, masterSources }) => {
  const points = trend?.points || [];
  const series = groupSources(trend?.sources || [], masterSources);
  const valueOf = (p, g) => g.members.reduce((s, m) => s + ((p.by_source || {})[m] || 0), 0);
  if (!points.length) return <Empty />;
  const max = Math.max(...points.map((p) => p.total), 1);
  const showEvery = points.length > 16 ? Math.ceil(points.length / 12) : 1;
  return (
    <div className="jd-trend">
      <ul className="jd-legend jd-legend-top">
        {series.map((g) => (
          <li key={g.key}><span className="jd-dot" style={{ background: g.color }} />{g.label}</li>
        ))}
      </ul>
      <div className="jd-cols">
        {points.map((p, idx) => (
          <div
            className="jd-col"
            key={p.period}
            title={`${formatPeriod(p.period, trend.granularity)} — ${fmt(p.total)} applications (${series
              .map((g) => `${g.label} ${fmt(valueOf(p, g))}`)
              .join(', ')})`}
          >
            <span className="jd-col-value">{p.total || ''}</span>
            <div className="jd-col-stack" style={{ height: `${(p.total / max) * 100}%` }}>
              {[...series].reverse().map((g) => {
                const v = valueOf(p, g);
                return v > 0 ? (
                  <span key={g.key} className="jd-col-part" style={{ flexGrow: v, background: g.color }} />
                ) : null;
              })}
            </div>
            <span className="jd-col-label">
              {idx % showEvery === 0 ? formatPeriod(p.period, trend.granularity) : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const StatusPill = ({ status }) => (
  <span className={`jd-pill pill-${status}`}>{humanize(status)}</span>
);

// ─────────────────────────────────────── page ───────────────────────────────────────

const Dashboard = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [stipendMonth, setStipendMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [sortBy, setSortBy] = useState({ key: 'total_applications', dir: 'desc' });
  const firstLoad = useRef(true);
  const [view, setView] = useState(readSavedView);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(null); // null | 'pdf' | 'csv'
  const [exportNote, setExportNote] = useState('');
  const reportRef = useRef(null);
  const exportMenuRef = useRef(null);

  // close the export menu when clicking anywhere else
  useEffect(() => {
    if (!exportOpen) return undefined;
    const close = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) setExportOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [exportOpen]);

  const changeView = (next) => {
    setView(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch {
      /* ignore storage errors */
    }
  };

  const fetchSummary = useCallback(async () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
    if (stipendMonth) params.append('stipend_month', stipendMonth);

    if (firstLoad.current) setLoading(true);
    else setRefreshing(true);

    try {
      const res = await fetch(`${API_BASE_URL}/api/dashboard/summary?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 401) {
        setError('Your session has expired. Please log in again.');
        return;
      }
      if (!res.ok || json.success === false) {
        throw new Error(json.message || `Request failed (${res.status})`);
      }
      setData(json.data);
      setError('');
    } catch (err) {
      setError(err.message || 'Unable to load dashboard');
    } finally {
      firstLoad.current = false;
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters, stipendMonth]);

  // Load on mount / whenever filters change
  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Keep it live: poll + refetch when the tab regains focus
  useEffect(() => {
    const id = setInterval(fetchSummary, AUTO_REFRESH_MS);
    const onFocus = () => document.visibilityState === 'visible' && fetchSummary();
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [fetchSummary]);

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  // If a filtered requirement / department / status was deleted or renamed after it was
  // selected, drop that filter automatically instead of showing an empty dashboard.
  useEffect(() => {
    const opts = data?.filter_options;
    if (!opts) return;
    const stale = {};
    const selectedReq = (opts.requirements || []).find((r) => String(r.id) === String(filters.requirement_id));
    if (
      filters.requirement_id &&
      (!selectedReq ||
        (filters.department && selectedReq.department && selectedReq.department !== filters.department) ||
        (filters.requirement_status && selectedReq.status && selectedReq.status !== filters.requirement_status))
    ) {
      stale.requirement_id = '';
    }
    if (filters.department && !(opts.departments || []).includes(filters.department)) {
      stale.department = '';
    }
    if (filters.requirement_status && !(opts.requirement_statuses || []).includes(filters.requirement_status)) {
      stale.requirement_status = '';
    }
    if (Object.keys(stale).length) setFilters((f) => ({ ...f, ...stale }));
  }, [data, filters.department, filters.requirement_status]); // eslint-disable-line react-hooks/exhaustive-deps
  const hasFilters = Object.values(filters).some(Boolean);

  // Requirement drop-down follows the department / status filters
  const requirementOptions = useMemo(
    () =>
      (data?.filter_options?.requirements || []).filter(
        (r) =>
          (!filters.department || r.department === filters.department) &&
          (!filters.requirement_status || r.status === filters.requirement_status)
      ),
    [data, filters.department, filters.requirement_status]
  );

  // Communication status columns = whatever statuses exist (sent, failed, pending, …)
  const commStatuses = useMemo(() => {
    const preferred = ['sent', 'delivered', 'read', 'pending', 'failed'];
    const found = new Set();
    (data?.communications || []).forEach((c) => Object.keys(c.statuses || {}).forEach((st) => found.add(st)));
    return [...preferred.filter((st) => found.has(st)), ...[...found].filter((st) => !preferred.includes(st)).sort()];
  }, [data]);

  const dateScope =
    filters.from_date || filters.to_date
      ? `${filters.from_date ? formatDate(filters.from_date) : 'start'} – ${filters.to_date ? formatDate(filters.to_date) : 'today'}`
      : 'all time';

  // Status columns in the requirements table: Applied / Training / Joined always,
  // any other status automatically once at least one application has it.
  const activeStatuses = useMemo(() => visiblePipeline(data?.pipeline).map((p) => p.key), [data]);
  const showVacancies = useMemo(() => hasVacancyData(data), [data]);

  // ── Export ──
  const VIEW_NAMES = { statistical: 'Statistical', graphical: 'Graphical' };
  const handleExportPdf = async (which) => {
    setExportOpen(false);
    if (!data || exporting) return;
    const original = view;
    const views = which === 'both' ? ['statistical', 'graphical'] : [which];
    setExporting('pdf');
    try {
      await exportPdf({
        views,
        showView: (v) => setView(v), // temporary switch, preference is not changed
        getContainer: () => reportRef.current,
        fileLabel: which === 'both' ? 'Full-Report' : VIEW_NAMES[which],
        onStep: (v) => setExportNote(`Preparing ${VIEW_NAMES[v].toLowerCase()} view…`),
      });
    } catch (err) {
      console.error('PDF export failed:', err);
      setError(`Could not create the PDF: ${err.message || err}`);
    } finally {
      setView(original);
      setExporting(null);
      setExportNote('');
    }
  };
  const handleExportCsv = () => {
    setExportOpen(false);
    if (!data) return;
    downloadCsv(data, filters, { statuses: activeStatuses, showVacancies });
  };

  const requirementRows = useMemo(() => {
    const rows = (data?.requirements || []).filter((r) => {
      if (!search.trim()) return true;
      const q = search.trim().toLowerCase();
      return [r.requirement_code, r.title, r.department, ...(r.locations || []).map((l) => l.name)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
    const { key, dir } = sortBy;
    const val = (r) => {
      if (key.startsWith('status:')) return r.status_counts[key.slice(7)] || 0;
      const v = r[key];
      return v == null ? -1 : v;
    };
    return [...rows].sort((a, b) => {
      const A = val(a);
      const B = val(b);
      const cmp = typeof A === 'string' ? A.localeCompare(B) : A - B;
      return dir === 'asc' ? cmp : -cmp;
    });
  }, [data, search, sortBy]);

  const toggleSort = (key) =>
    setSortBy((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  const SortTh = ({ k, children, className = '' }) => (
    <th className={`is-sortable ${className}`} onClick={() => toggleSort(k)}>
      {children}
      {sortBy.key === k && <i className={`fas fa-caret-${sortBy.dir === 'asc' ? 'up' : 'down'}`} />}
    </th>
  );

  if (loading && !data) {
    return (
      <div className="jd-page">
        <div className="jd-loading"><span className="jd-spinner" /> Loading dashboard…</div>
      </div>
    );
  }

  const k = data?.kpis || {};
  const totalApps = k.applications_total || 0;

  return (
    <div className={`jd-page ${exporting === 'pdf' ? 'jd-exporting' : ''}`}>
      {/* ── Header ── */}
      <div className="jd-header">
        <div>
          <h1>Recruitment Dashboard</h1>
          <p>
            Live overview of every requirement and application
            {data?.generated_at && (
              <> · Updated {new Date(data.generated_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</>
            )}
          </p>
        </div>
        <div className="jd-header-actions">
          <div className="jd-toggle" role="tablist" aria-label="Dashboard view">
            <button type="button" role="tab" aria-selected={view === 'statistical'}
              className={view === 'statistical' ? 'is-active' : ''} onClick={() => changeView('statistical')}>
              <i className="fas fa-table" /> Statistical
            </button>
            <button type="button" role="tab" aria-selected={view === 'graphical'}
              className={view === 'graphical' ? 'is-active' : ''} onClick={() => changeView('graphical')}>
              <i className="fas fa-chart-pie" /> Graphical
            </button>
          </div>
          <div className="jd-export" ref={exportMenuRef}>
            <button type="button" className="jd-btn jd-btn-ghost" onClick={() => setExportOpen((o) => !o)}
              disabled={!data || !!exporting} aria-haspopup="menu" aria-expanded={exportOpen}>
              <i className={`fas ${exporting ? 'fa-spinner fa-spin' : 'fa-download'}`} /> {exporting ? 'Exporting' : 'Export'}
              <i className="fas fa-chevron-down jd-export-caret" />
            </button>
            {exportOpen && (
              <div className="jd-export-menu" role="menu">
                <div className="jd-export-group">PDF report</div>
                <button type="button" role="menuitem" onClick={() => handleExportPdf('statistical')}>
                  <i className="fas fa-table" /> Statistical view
                </button>
                <button type="button" role="menuitem" onClick={() => handleExportPdf('graphical')}>
                  <i className="fas fa-chart-pie" /> Graphical view
                </button>
                <button type="button" role="menuitem" onClick={() => handleExportPdf('both')}>
                  <i className="fas fa-file-pdf" /> Both views (full report)
                </button>
                <div className="jd-export-group">Data</div>
                <button type="button" role="menuitem" onClick={handleExportCsv}>
                  <i className="fas fa-file-excel" /> Excel / CSV (all tables)
                </button>
              </div>
            )}
          </div>
          <button type="button" className="jd-btn" onClick={fetchSummary} disabled={refreshing}>
            <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`} /> {refreshing ? 'Refreshing' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="jd-filters">
        <label>
          <span>From</span>
          <input type="date" value={filters.from_date} max={filters.to_date || undefined}
            onChange={(e) => setFilter('from_date', e.target.value)} />
        </label>
        <label>
          <span>To</span>
          <input type="date" value={filters.to_date} min={filters.from_date || undefined}
            onChange={(e) => setFilter('to_date', e.target.value)} />
        </label>
        <label>
          <span>Department</span>
          <select value={filters.department} onChange={(e) => setFilter('department', e.target.value)}>
            <option value="">All departments</option>
            {(data?.filter_options?.departments || []).map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Requirement status</span>
          <select value={filters.requirement_status} onChange={(e) => setFilter('requirement_status', e.target.value)}>
            <option value="">All statuses</option>
            {(data?.filter_options?.requirement_statuses || []).map((s) => (
              <option key={s} value={s}>{humanize(s)}</option>
            ))}
          </select>
        </label>
        <label className="jd-filter-wide">
          <span>Requirement</span>
          <select value={filters.requirement_id} onChange={(e) => setFilter('requirement_id', e.target.value)}>
            <option value="">All requirements</option>
            {requirementOptions.map((r) => (
              <option key={r.id} value={r.id}>{r.requirement_code} — {r.title}</option>
            ))}
          </select>
        </label>
        {hasFilters && (
          <button type="button" className="jd-btn jd-btn-ghost" onClick={() => setFilters(EMPTY_FILTERS)}>
            <i className="fas fa-times" /> Clear
          </button>
        )}
      </div>

      {error && (
        <div className="jd-error">
          <i className="fas fa-exclamation-triangle" /> {error}
          <button type="button" onClick={fetchSummary}>Retry</button>
        </div>
      )}

      <div ref={reportRef} className="jd-report">
      {data && exporting === 'pdf' && (
        <div className="jd-report-head">
          <div>
            <h2>JMK Recruitment Dashboard — {VIEW_NAMES[view]} view</h2>
            <p>{describeFilters(filters, data)}</p>
          </div>
          <span>Generated {new Date().toLocaleString('en-IN')}</span>
        </div>
      )}
      {data && view === 'graphical' && (
        <GraphicalDashboard
          data={data}
          onNavigate={navigate}
          stipendMonth={stipendMonth}
          onStipendMonthChange={(m) => setStipendMonth(m || currentMonth())}
          onPickRequirement={(id) => {
            setFilter('requirement_id', id);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      )}

      {data && view === 'statistical' && (
        <>
          {/* ── KPIs ── */}
          <div className="jd-kpis">
            <Kpi icon="fa-briefcase" label="Requirements" value={fmt(k.requirements_total)}
              sub={`${fmt(k.requirements_open)} open`} onClick={() => navigate('/admin/view-requirement')} />
            {showVacancies && (
              <Kpi icon="fa-chair" label="Vacancies remaining" value={fmt(k.vacancies_remaining)}
                sub={`of ${fmt(k.vacancies_total)} total`} />
            )}
            <Kpi icon="fa-file-alt" label="Applications" value={fmt(k.applications_total)}
              sub={dateScope} onClick={() => navigate('/admin/view-applications')} />
            <Kpi icon="fa-users" label="Candidates" value={fmt(k.candidates_total)}
              sub={`All registered · +${fmt(k.candidates_last_30_days)} in last 30 days`} onClick={() => navigate('/admin/candidates')} />
            <Kpi icon="fa-chalkboard-teacher" label="In training" value={fmt(k.in_training)}
              sub={`${pct(k.in_training, totalApps)}% of applications`} />
            <Kpi icon="fa-user-check" label="Joined" value={fmt(k.joined)}
              sub={`${pct(k.joined, totalApps)}% of applications`} />
            <Kpi icon="fa-ticket-alt" label="Open tickets" value={fmt(k.tickets_open)}
              sub="All tickets · open + in progress" onClick={() => navigate('/admin/tickets')} />
          </div>

          {/* ── Pipeline + trend ── */}
          <div className="jd-grid jd-grid-2">
            <Card title="Hiring pipeline" subtitle={`${fmt(totalApps)} applications by current status`}>
              <BarList items={visiblePipeline(data.pipeline)} total={totalApps} muted={['rejected', 'withdrawn']} />
            </Card>
            <Card
              title="Applications over time"
              subtitle={data.trend.granularity === 'day' ? 'Daily, selected range' : 'Monthly, last 12 months'}
            >
              <TrendChart trend={data.trend} masterSources={data.filter_options?.sources} />
            </Card>
          </div>

          {/* ── All requirements ── */}
          <Card
            className="jd-card-table"
            title="All requirements"
            subtitle={`${fmt(requirementRows.length)} of ${fmt(data.requirements.length)} requirements · click a row for location-wise details`}
            action={
              <div className="jd-search">
                <i className="fas fa-search" />
                <input placeholder="Search code, title, department, location…" value={search}
                  onChange={(e) => setSearch(e.target.value)} />
              </div>
            }
          >
            {requirementRows.length === 0 ? (
              <Empty text={data.requirements.length ? 'No requirement matches your search' : 'No requirements created yet'} />
            ) : (
              <div className="jd-table-wrap">
                <table className="jd-table">
                  <thead>
                    <tr>
                      <SortTh k="requirement_code">Code</SortTh>
                      <SortTh k="title">Title</SortTh>
                      <SortTh k="department">Department</SortTh>
                      <SortTh k="status">Status</SortTh>
                      <th className="num">Locations</th>
                      {showVacancies && <SortTh k="vacancies" className="num">Vacancies</SortTh>}
                      {showVacancies && <SortTh k="remaining_vacancy" className="num">Remaining</SortTh>}
                      <SortTh k="total_applications" className="num">Applications</SortTh>
                      {activeStatuses.map((s) => (
                        <SortTh key={s} k={`status:${s}`} className="num">{humanize(s)}</SortTh>
                      ))}
                      {showVacancies && <th>Fill</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {requirementRows.map((r) => {
                      const isOpen = expanded === r.id;
                      const fill = r.vacancies ? pct(r.filled, r.vacancies) : null;
                      return (
                        <React.Fragment key={r.id}>
                          <tr className={`jd-row ${isOpen ? 'is-open' : ''}`} onClick={() => setExpanded(isOpen ? null : r.id)}>
                            <td className="mono">
                              <i className={`fas fa-chevron-${isOpen ? 'down' : 'right'} jd-caret`} />
                              {r.requirement_code}
                            </td>
                            <td className="jd-title-cell">{r.title}</td>
                            <td>{r.department || '—'}</td>
                            <td><StatusPill status={r.status} /></td>
                            <td className="num">{r.locations.length}</td>
                            {showVacancies && <td className="num">{r.vacancies == null ? '—' : fmt(r.vacancies)}</td>}
                            {showVacancies && (
                              <td className="num">{r.remaining_vacancy == null ? '—' : fmt(r.remaining_vacancy)}</td>
                            )}
                            <td className="num strong">{fmt(r.total_applications)}</td>
                            {activeStatuses.map((s) => (
                              <td key={s} className="num">{r.status_counts[s] ? fmt(r.status_counts[s]) : '·'}</td>
                            ))}
                            {showVacancies && <td>
                              {fill == null ? (
                                <span className="jd-muted">No vacancy set</span>
                              ) : (
                                <span className="jd-meter" title={`${fmt(r.filled)} joined of ${fmt(r.vacancies)} vacancies`}>
                                  <span className="jd-meter-track"><span style={{ width: `${Math.min(fill, 100)}%` }} /></span>
                                  <em>{fill}%</em>
                                </span>
                              )}
                            </td>}
                          </tr>
                          {isOpen && (
                            <tr className="jd-detail">
                              <td colSpan={(showVacancies ? 9 : 6) + activeStatuses.length}>
                                <div className="jd-detail-meta">
                                  <span><strong>Job type:</strong> {r.job_type ? humanize(r.job_type) : '—'}</span>
                                  <span><strong>Experience:</strong>{' '}
                                    {r.experience_min != null || r.experience_max != null
                                      ? `${r.experience_min ?? 0} – ${r.experience_max ?? '∞'} yrs` : '—'}</span>
                                  <span><strong>Created:</strong> {formatDate(r.created_at)}</span>
                                  {r.closed_at && <span><strong>Closed:</strong> {formatDate(r.closed_at)}</span>}
                                </div>
                                {r.locations.length ? (
                                  <table className="jd-subtable">
                                    <thead>
                                      <tr>
                                        <th>Location</th><th>Open</th>
                                        {showVacancies && <th className="num">Vacancies</th>}
                                        <th className="num">Applications</th><th className="num">Training</th>
                                        <th className="num">Joined</th>
                                        {showVacancies && <th className="num">Remaining</th>}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {r.locations.map((l) => (
                                        <tr key={l.name}>
                                          <td>{l.name}</td>
                                          <td>{l.is_open ? <span className="jd-yes">Yes</span> : <span className="jd-no">No</span>}</td>
                                          {showVacancies && (
                                            <td className="num">{l.vacancy_count == null ? '—' : fmt(l.vacancy_count)}</td>
                                          )}
                                          <td className="num">{fmt(l.applications_count)}</td>
                                          <td className="num">{fmt(l.training_count)}</td>
                                          <td className="num">{fmt(l.joined_count)}</td>
                                          {showVacancies && (
                                            <td className="num">{l.remaining_vacancy == null ? '—' : fmt(l.remaining_vacancy)}</td>
                                          )}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                ) : (
                                  <span className="jd-muted">No locations configured for this requirement.</span>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* ── Department + location ── */}
          <div className="jd-grid jd-grid-2">
            <Card title="Applications by department" subtitle="Across the filtered requirements">
              <BarList
                items={data.departments.map((d) => ({
                  key: d.name,
                  label: `${d.name} (${d.requirements} req)`,
                  count: d.applications,
                }))}
              />
            </Card>
            <Card
              title={showVacancies ? 'Location-wise vacancies' : 'Location-wise applications'}
              subtitle={showVacancies ? 'Vacancies vs applications and joinings' : 'Applications and joinings per location'}
            >
              {data.locations.length ? (
                <div className="jd-table-wrap jd-scroll">
                  <table className="jd-table jd-table-compact">
                    <thead>
                      <tr>
                        <th>Location</th><th className="num">Req.</th>
                        {showVacancies && <th className="num">Vacancies</th>}
                        <th className="num">Applications</th><th className="num">Joined</th>
                        {showVacancies && <th>Filled</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {data.locations.map((l) => {
                        const fill = l.vacancies ? pct(l.joined, l.vacancies) : null;
                        return (
                          <tr key={l.name}>
                            <td>{l.name}</td>
                            <td className="num">{fmt(l.requirements)}</td>
                            {showVacancies && <td className="num">{l.vacancies ? fmt(l.vacancies) : '—'}</td>}
                            <td className="num">{fmt(l.applications)}</td>
                            <td className="num">{fmt(l.joined)}</td>
                            {showVacancies && <td>
                              {fill == null ? <span className="jd-muted">—</span> : (
                                <span className="jd-meter">
                                  <span className="jd-meter-track"><span style={{ width: `${Math.min(fill, 100)}%` }} /></span>
                                  <em>{fill}%</em>
                                </span>
                              )}
                            </td>}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : <Empty />}
            </Card>
          </div>

          {/* ── Joining, documents, payments ── */}
          <div className="jd-grid jd-grid-3">
            <Card title="Application source" subtitle="Where candidates applied from">
              <Segments items={groupSourceCounts(data.sources, data.filter_options?.sources)} />
            </Card>
            <Card title="Documents (training + joined)" subtitle="Submission status reported by candidates">
              <Segments
                items={data.documents}
                tones={{ submitted: 'teal', partially_submitted: 'amber', not_submitted: 'gray' }}
              />
            </Card>
            <Card title="Joining form" subtitle="Job application form of joined candidates">
              <Segments
                items={data.joined_forms}
                tones={{ completed: 'teal', in_progress: 'amber', not_started: 'gray' }}
              />
            </Card>
          </div>

          <div className="jd-grid jd-grid-3">
            <Card
              title="Stipend / salary payments"
              subtitle="Recorded for the selected month"
              action={
                <input type="month" className="jd-month" value={stipendMonth}
                  onChange={(e) => setStipendMonth(e.target.value || currentMonth())} />
              }
            >
              <div className="jd-stats">
                <div><span>Records</span><strong>{fmt(data.stipends.records)}</strong></div>
                <div><span>Total amount</span><strong>₹{fmt(data.stipends.total_amount)}</strong></div>
                <div><span>Govt. received</span><strong>{fmt(data.stipends.govt_received)}</strong></div>
                <div><span>Govt. pending</span><strong>{fmt(data.stipends.govt_not_received)}</strong></div>
                <div><span>Paid to candidate</span><strong>{fmt(data.stipends.candidate_paid)}</strong></div>
                <div><span>Not paid</span><strong>{fmt(data.stipends.candidate_not_paid)}</strong></div>
              </div>
            </Card>
            <Card title="Support tickets" subtitle="All tickets by status"
              action={<button type="button" className="jd-link" onClick={() => navigate('/admin/tickets')}>Open</button>}>
              <BarList items={data.tickets} muted={['resolved', 'closed']} />
            </Card>
            <Card title="Communications" subtitle="Messages sent to candidates">
              {data.communications.length ? (
                <table className="jd-table jd-table-compact">
                  <thead>
                    <tr>
                      <th>Channel</th><th className="num">Total</th>
                      {commStatuses.map((st) => <th key={st} className="num">{humanize(st)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {data.communications.map((c) => (
                      <tr key={c.channel}>
                        <td>{humanize(c.channel)}</td>
                        <td className="num">{fmt(c.total)}</td>
                        {commStatuses.map((st) => (
                          <td key={st} className="num">{fmt((c.statuses || {})[st])}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <Empty text="No messages sent yet" />}
            </Card>
          </div>

          {/* ── Recent ── */}
          <Card title="Latest applications" subtitle="Most recent 8"
            action={<button type="button" className="jd-link" onClick={() => navigate('/admin/view-applications')}>View all</button>}>
            {data.recent_applications.length ? (
              <div className="jd-table-wrap">
                <table className="jd-table jd-table-compact">
                  <thead>
                    <tr><th>Candidate</th><th>Requirement</th><th>Location</th><th>Source</th><th>Status</th><th>Applied</th></tr>
                  </thead>
                  <tbody>
                    {data.recent_applications.map((a) => (
                      <tr key={a.id}>
                        <td className="strong">{a.candidate_name || '—'}</td>
                        <td><span className="mono">{a.requirement_code}</span> {a.requirement_title}</td>
                        <td>{a.preferred_location || '—'}</td>
                        <td>{humanize(a.source)}</td>
                        <td><StatusPill status={a.status} /></td>
                        <td>{formatDate(a.applied_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <Empty />}
          </Card>
        </>
      )}
      </div>

      {exporting === 'pdf' && (
        <div className="jd-export-overlay" data-export-skip>
          <div><span className="jd-spinner" /> {exportNote || 'Preparing PDF…'}</div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
